import { HelpCircle } from "lucide-react";
import { useQuery } from "@tanstack/react-query";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/popover";
import { useTourContext } from "./TourContext";
import { apiRequest } from "@/lib/queryClient";
import type { RuntimeTour } from "./use-tour";

interface AvailableTour {
  id: string;
  title: string;
  description: string | null;
}

interface AvailableForRouteResponse {
  tours: AvailableTour[];
}

interface SeenStateEntry {
  seenAt: string | null;
  dismissedAt: string | null;
  lastStepIndex: number | null;
}

interface SeenStateResponse {
  state: Record<string, SeenStateEntry>;
}

// P23C: if the current route has multiple eligible tours, show a catalogue
// popover so the user can choose which one to launch. If only one tour
// matches (or none), behaviour is unchanged from P23a/b.
export function TourHelpButton() {
  const { tour, isRunning, hasBeenSeen, trigger, triggerById } = useTourContext();

  // Use the current path from the window to query the catalogue.
  const currentPath = typeof window !== "undefined" ? window.location.pathname : null;

  const catalogueQuery = useQuery<AvailableForRouteResponse>({
    queryKey: ["/api/tours/available-for-route", currentPath],
    enabled: !!currentPath,
    queryFn: async () => {
      const url = `/api/tours/available-for-route?path=${encodeURIComponent(currentPath!)}`;
      return (await apiRequest("GET", url)) as AvailableForRouteResponse;
    },
    staleTime: 30_000,
  });

  const seenQuery = useQuery<SeenStateResponse>({
    queryKey: ["/api/tours/seen"],
  });

  const availableTours = catalogueQuery.data?.tours ?? [];

  // If there's no tour for this route at all, render nothing.
  if (!tour && availableTours.length === 0) return null;

  // If there's only one matching tour, keep the existing direct-trigger UX.
  if (availableTours.length <= 1) {
    if (!tour) return null;
    const label = hasBeenSeen
      ? `Re-run tour: ${tour.title}`
      : `Start tour: ${tour.title}`;
    return (
      <Button
        size="icon"
        variant="ghost"
        onClick={() => trigger()}
        disabled={isRunning}
        data-testid="button-tour-help"
        aria-label={label}
        title={hasBeenSeen ? "Re-run tour" : "Start tour"}
      >
        <HelpCircle className="h-4 w-4" />
      </Button>
    );
  }

  // Multiple tours — show the catalogue popover.
  const seenState = seenQuery.data?.state ?? {};
  return (
    <Popover>
      <PopoverTrigger asChild>
        <Button
          size="icon"
          variant="ghost"
          disabled={isRunning}
          data-testid="button-tour-help"
          aria-label="Tour catalogue for this page"
          title="Tours for this page"
        >
          <HelpCircle className="h-4 w-4" />
        </Button>
      </PopoverTrigger>
      <PopoverContent
        align="end"
        className="w-72 p-2"
        style={{ zIndex: 200 }}
        data-testid="popover-tour-catalogue"
      >
        <p className="text-xs font-semibold text-muted-foreground uppercase tracking-wide px-1 mb-1">
          Tours for this page
        </p>
        <ul className="space-y-0.5">
          {availableTours.map((t) => {
            const entry = seenState[t.id];
            const seen = !!(entry?.seenAt || entry?.dismissedAt);
            return (
              <li key={t.id}>
                <button
                  type="button"
                  onClick={() => triggerById(t.id)}
                  disabled={isRunning}
                  data-testid={`button-tour-catalogue-${t.id}`}
                  className="w-full text-left px-2 py-1.5 rounded-md text-sm hover-elevate flex items-center justify-between gap-2"
                >
                  <span className="font-medium truncate">{t.title}</span>
                  {seen && (
                    <Badge variant="secondary" className="shrink-0 text-xs">
                      Seen
                    </Badge>
                  )}
                </button>
              </li>
            );
          })}
        </ul>
      </PopoverContent>
    </Popover>
  );
}
